import { Component, inject, signal, resource, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppCard } from '../../shared/components/app-card/app-card';
import { StatisticsService } from '../../core/services/statistics.service';
import { BusinessContextService } from '../../core/services/business-context.service';
import { firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { AppButton } from '../../shared/components/app-button/app-button';
import { RouterModule } from '@angular/router';
import { AppSectionHeader } from "../../shared/components/app-section-header/app-section-header";
import { InventoryService } from '../../shared/services/inventory.service';
import { DialogService } from '../../core/services/dialog.service';
import { InventoryTransactionDialog } from '../inventory/catalog/components/transaction-dialog/transaction-dialog';
import { Product } from '../../shared/models/inventory.models';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-clinical-dashboard',
  standalone: true,
  imports: [CommonModule, AppCard, FormsModule, AppButton, RouterModule, AppSectionHeader],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './clinical-dashboard.html',
})
export class ClinicalDashboard {
  private statsService = inject(StatisticsService);
  public bizCtx = inject(BusinessContextService);
  public authService = inject(AuthService);
  private inventoryService = inject(InventoryService);
  private dialog = inject(DialogService);

  // Filtros de fecha: Default 1 año atrás hasta hoy
  startDate = signal(this.getPastDate(1, 'year'));
  endDate = signal(this.getTodayDate());

  // Recurso reactivo para estadísticas
  statsResource = resource({
    loader: async () => {
      // Si el negocio está bloqueado, no realizamos la petición
      if (this.bizCtx.isBlocked()) return null;
      return firstValueFrom(this.statsService.getDashboardStats(this.startDate(), this.endDate()));
    }
  });

  inventoryResource = resource({
    loader: async () => {
      if (this.bizCtx.isBlocked()) return null;
      if (!this.bizCtx.hasPermission('inventory:view') && !this.bizCtx.hasPermission('inventory:manage')) return null;
      try {
        return await firstValueFrom(this.inventoryService.getDashboardMetrics());
      } catch (e) {
        return null;
      }
    }
  });

  get maxChartValue(): number {
    const data = this.statsResource.value()?.chartData || [];
    if (data.length === 0) return 20;
    return Math.max(...data.map(d => d.value), 20);
  }

  get totalRevenueByCategory(): number {
    const data = this.statsResource.value()?.revenueByCategory || [];
    return data.reduce((acc, d) => acc + d.value, 0);
  }

  get maxPatientGrowthValue(): number {
    const data = this.statsResource.value()?.patientGrowth || [];
    if (data.length === 0) return 10;
    return Math.max(...data.map(d => d.value), 10);
  }

  private getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private getPastDate(amount: number, unit: 'day' | 'month' | 'year'): string {
    const d = new Date();
    if (unit === 'year') d.setFullYear(d.getFullYear() - amount);
    if (unit === 'month') d.setMonth(d.getMonth() - amount);
    if (unit === 'day') d.setDate(d.getDate() - amount);
    return d.toISOString().split('T')[0];
  }

  updateFilters(start: string, end: string) {
    this.startDate.set(start);
    this.endDate.set(end);
  }

  reload() {
    this.statsResource.reload();
    this.inventoryResource.reload();
  }

  // Inventory Helpers
  reorder(product: Product) {
    this.dialog.open(InventoryTransactionDialog, {
      title: 'Reponer Stock',
      description: `Ingresar nueva compra o ajuste para ${product.name}`,
      icon: 'solar:box-bold-duotone',
      width: '500px',
      data: { product, type: 'IN' }
    }).afterClosed().subscribe(res => {
      if (res?.action === 'saved') {
        this.inventoryResource.reload();
      }
    });
  }

  getTransactionTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      'IN': 'Ingreso',
      'OUT': 'Salida',
      'ADJUSTMENT': 'Ajuste',
      'DEDUCTION': 'Consumo'
    };
    return labels[type] || type;
  }

  getTransactionTypeClass(type: string): string {
    const classes: Record<string, string> = {
      'IN': 'bg-emerald-500/10 text-emerald-600',
      'OUT': 'bg-rose-500/10 text-rose-600',
      'ADJUSTMENT': 'bg-orange-500/10 text-orange-600',
      'DEDUCTION': 'bg-sky-500/10 text-sky-600'
    };
    return classes[type] || '';
  }
}
